import {
  Component,
  EventEmitter,
  Input,
  Output,
  HostListener,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

/** Search item data structure */
export interface SearchItem {
  id: string;
  label: string;
  icon: string;
  type: 'tab' | 'action';
  shortcut?: string;
  children?: SearchItem[];
  date?: string;
  route?: string;
}

/**
 * Search Overlay Component
 *
 * Command palette for quick navigation and search.
 * Supports keyboard navigation, drill-down, and filtering.
 *
 * @component
 * @example
 * <app-search-overlay [isOpen]="true" (close)="handleClose()"></app-search-overlay>
 */
@Component({
  selector: 'app-search-overlay',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './search-overlay.component.html',
  styleUrls: ['./search-overlay.component.scss']
})
export class SearchOverlayComponent implements OnChanges {
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  /** Controls overlay visibility */
  @Input() isOpen = false;

  /** Emits when overlay is closed */
  @Output() close = new EventEmitter<void>();

  /** Reference to search input element */
  @ViewChild('searchInput') searchInput!: ElementRef;

  /** Search filter text */
  searchTerm = '';

  /** Currently selected item index */
  selectedIndex = 0;

  /** Current parent item when drilled down */
  selectedParent: SearchItem | null = null;

  /**
   * Lifecycle hook - auto-focuses search input when opened
   * @param changes - Component input changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen'] && changes['isOpen'].currentValue) {
      this.selectedIndex = 0;
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
      }, 0);
    }
  }

  /** Available search items with hierarchical structure */
  items: SearchItem[] = [
    // {
    //   id: '1',
    //   label: 'Applications',
    //   icon: 'grid',
    //   type: 'action',
    //   shortcut: 'Tab',
    //   route: 'technicaldesign/appsettings',
    //   children: [
    //     { id: '1-1', label: 'Applications 1', icon: 'grid', type: 'action', date: '1 day ago' },
    //     { id: '1-2', label: 'Applications 2', icon: 'grid', type: 'action', date: '3 days ago' },
    //     { id: '1-3', label: 'Applications 3', icon: 'grid', type: 'action', date: '1 week ago' },
    //     { id: '1-4', label: 'Applications 4', icon: 'grid', type: 'action', date: '2 weeks ago' }
    //   ]
    // },
    {
      id: '3',
      label: 'Roles',
      icon: 'users',
      type: 'action',
      shortcut: 'Tab',
      route: 'gathering/rolesandpermissions',
      children: [
        { id: '3-1', label: 'Roles 1', icon: 'users', type: 'action', date: '1 month ago' },
        { id: '3-2', label: 'Roles 2', icon: 'users', type: 'action', date: '1 month ago' }
      ]
    },
    {
      id: '4',
      label: 'Data Models',
      icon: 'database',
      type: 'action',
      shortcut: 'Tab',
      route: 'designs/datamodel',
      children: [
        { id: '4-1', label: 'Data Model 1', icon: 'database', type: 'action', date: '2 days ago' },
        { id: '4-2', label: 'Data Model 2', icon: 'database', type: 'action', date: '1 week ago' },
        { id: '4-3', label: 'Data Model 3', icon: 'database', type: 'action', date: '2 weeks ago' }
      ]
    },
    {
      id: '5',
      label: 'Pages',
      icon: 'layout',
      type: 'action',
      shortcut: 'Tab',
      route: 'prototyping/screens',
      children: [
        { id: '5-1', label: 'Page 1', icon: 'layout', type: 'action', date: '1 hour ago' },
        { id: '5-2', label: 'Page 2', icon: 'layout', type: 'action', date: '3 hours ago' },
        { id: '5-3', label: 'Page 3', icon: 'layout', type: 'action', date: '1 day ago' }
      ]
    },
    {
      id: '6',
      label: 'Workflows',
      icon: 'git-merge',
      type: 'action',
      shortcut: 'Tab',
      route: 'designs/workflow',
      children: [
        { id: '6-1', label: 'Workflow 1', icon: 'git-merge', type: 'action', date: '4 days ago' },
        { id: '6-2', label: 'Workflow 2', icon: 'git-merge', type: 'action', date: '1 week ago' }
      ]
    },
    {
      id: '7',
      label: 'Custom Code',
      icon: 'code',
      type: 'action',
      shortcut: 'Tab',
      route: 'prototyping/customcomponents',
      children: [
        { id: '7-1', label: 'Custom Code 1', icon: 'code', type: 'action', date: '2 weeks ago' },
        { id: '7-2', label: 'Custom Code 2', icon: 'code', type: 'action', date: '3 weeks ago' }
      ]
    }
  ];

  /**
   * Returns filtered items based on search term and current context
   */
  get filteredItems(): SearchItem[] {
    let currentItems = this.selectedParent ? this.selectedParent.children || [] : this.items;

    if (this.searchTerm) {
      currentItems = currentItems.filter((item) => item.label.toLowerCase().includes(this.searchTerm.toLowerCase()));
    }
    return currentItems;
  }

  /**
   * Closes overlay and resets state
   */
  onClose() {
    this.close.emit();
    this.resetState();
  }

  /**
   * Resets search state to initial values
   */
  resetState() {
    this.searchTerm = '';
    this.selectedIndex = 0;
    this.selectedParent = null;
  }

  /**
   * Handles backdrop click to close overlay
   * @param event - Mouse click event
   */
  onBackdropClick(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('overlay-backdrop')) {
      this.onClose();
    }
  }

  /**
   * Handles item selection
   * @param item - Selected search item
   */
  selectItem(item: SearchItem) {
    if (item.route) {
      this.router.navigate([item.route], { relativeTo: this.route });
      this.onClose();
    } else if (this.selectedParent && this.selectedParent.route) {
      // If a child item is selected, navigate to the parent's route
      this.router.navigate([this.selectedParent.route], { relativeTo: this.route });
      this.onClose();
    } else if (item.children && item.children.length > 0) {
      this.selectedParent = item;
      this.selectedIndex = 0;
      this.searchTerm = '';
    }
  }

  /**
   * Keyboard navigation handler
   * Supports: Arrow keys, Tab (drill-down), Backspace (go back), Escape, Enter
   * @param event - Keyboard event
   */
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.isOpen) return;

    const items = this.filteredItems;

    switch (event.key) {
      case 'Escape':
        if (this.selectedParent) {
          this.selectedParent = null;
          this.selectedIndex = 0;
          this.searchTerm = '';
          event.preventDefault();
        } else {
          this.onClose();
        }
        break;

      case 'ArrowDown':
        this.selectedIndex = (this.selectedIndex + 1) % items.length;
        event.preventDefault();
        break;

      case 'ArrowUp':
        this.selectedIndex = (this.selectedIndex - 1 + items.length) % items.length;
        event.preventDefault();
        break;

      case 'Tab':
        if (items[this.selectedIndex] && !this.selectedParent) {
          const item = items[this.selectedIndex];
          if (item.children && item.children.length > 0) {
            this.selectedParent = item;
            this.selectedIndex = 0;
            this.searchTerm = '';
          }
          event.preventDefault();
        }
        break;

      case 'Enter':
        if (items[this.selectedIndex]) {
          this.selectItem(items[this.selectedIndex]);
        }
        break;

      case 'Backspace':
        if (this.searchTerm === '' && this.selectedParent) {
          this.selectedParent = null;
          this.selectedIndex = 0;
          event.preventDefault();
        }
        break;
    }
  }
}
